import { Row } from '@tanstack/react-table';
import { TriangleAlertIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';
import { parseFormNumber } from '@/helpers/finance';

import { useHasZeroAmountError } from '../../../hooks/generate-bills/use-has-zero-amount-error';
import {
  GenerateBillAddedProjectListItem,
  GenerateBillListItem,
  GenerateBillMemberOverheadListItem,
} from '../../../types';
import {
  getBillFieldName,
  isAddedProjectSubRow,
  isAddOverheadSubRow,
  isOverheadSubRow,
  isSalaryAdjustmentSubRow,
} from '../bill-row.helpers';

/** Fixes the warning glyph so both call sites below only have to supply the message. */
const WarningIcon = ({ children }: { children: React.ReactNode }) => (
  <SimpleTooltip content={children}>
    <TriangleAlertIcon size={16} className="shrink-0 text-destructive" />
  </SimpleTooltip>
);

/**
 * Aggregates every sub-row validation problem onto the member row, so a collapsed member still
 * shows that something inside needs attention.
 *
 * Three distinct classes drive it:
 *   - project / added-project / time-off rows whose hours AND rate are both 0
 *   - overhead rows with an empty description
 *   - added-project rows that haven't picked a project yet
 *
 * Overheads are excluded from the zero-amount check because they display a fixed "1 hour" and
 * intentionally carry only a rate.
 */
export const MemberZeroAmountWarning = ({ row }: { row: Row<GenerateBillListItem> }) => {
  const formContext = useFormContext();

  const numericSubRows = row.subRows.filter(
    sr => !isAddOverheadSubRow(sr) && !isOverheadSubRow(sr) && !isSalaryAdjustmentSubRow(sr)
  );
  const overheadSubRows = row.subRows.filter(sr => isOverheadSubRow(sr));
  const addedProjectSubRows = row.subRows.filter(sr => isAddedProjectSubRow(sr));

  const hoursValues = useWatch({
    control: formContext.control,
    name: numericSubRows.map(subRow => getBillFieldName(subRow, 'hours')),
  });
  const rateValues = useWatch({
    control: formContext.control,
    name: numericSubRows.map(subRow => getBillFieldName(subRow, 'effectiveRate')),
  });
  const descriptionValues = useWatch({
    control: formContext.control,
    name: overheadSubRows.map(sr => getBillFieldName(sr, 'description')),
  });
  const projectIdValues = useWatch({
    control: formContext.control,
    name: addedProjectSubRows.map(sr => getBillFieldName(sr, 'projectId')),
  });

  const hasZeroAmountSelected = numericSubRows.some((subRow, idx) => {
    if (!subRow.getIsSelected()) return false;
    const hours = parseFormNumber(hoursValues?.[idx] ?? subRow.original.hours);
    const rate = parseFormNumber(rateValues?.[idx] ?? subRow.original.effectiveRate);
    return hours === 0 && rate === 0;
  });

  const hasEmptyOverheadName = overheadSubRows.some((subRow, idx) => {
    if (!subRow.getIsSelected()) return false;
    const overhead = subRow.original as unknown as GenerateBillMemberOverheadListItem;
    const description = String(descriptionValues?.[idx] ?? overhead.description ?? '');
    return description.trim() === '';
  });

  const hasMissingProject = addedProjectSubRows.some((subRow, idx) => {
    if (!subRow.getIsSelected()) return false;
    const added = subRow.original as unknown as GenerateBillAddedProjectListItem;
    const projectId = projectIdValues?.[idx] ?? added.projectId;
    return projectId === null || projectId === undefined || projectId === '';
  });

  const tooltipMessages = [
    hasZeroAmountSelected && i18n.t('authenticated.bills:new.validation.zeroAmountWarning'),
    hasEmptyOverheadName && i18n.t('authenticated.bills:new.validation.emptyOverheadNameWarning'),
    hasMissingProject && i18n.t('authenticated.bills:new.validation.missingProjectWarning'),
  ].filter((message): message is string => Boolean(message));

  if (tooltipMessages.length === 0) return null;

  return (
    <WarningIcon>
      <div className="flex flex-col gap-1">
        {tooltipMessages.map(message => (
          <span key={message}>{message}</span>
        ))}
      </div>
    </WarningIcon>
  );
};

/** Shown next to a sub-row's name when both its hours and rate are 0. */
export const ProjectZeroAmountError = ({ row }: { row: Row<GenerateBillListItem> }) => {
  const hasZeroError = useHasZeroAmountError(row);

  if (!hasZeroError) return null;

  return (
    <WarningIcon>{i18n.t('authenticated.bills:new.validation.zeroAmountProjectError')}</WarningIcon>
  );
};
